import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Star, Users, TrendingUp, Globe } from 'lucide-react';
import Layout from '@/components/Layout';
import { ECOSYSTEM_STEPS, COMPANIES, ROUTE_PATHS, companyPath } from '@/lib/index';

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const ICONS = { Star, Users, TrendingUp, Globe, Sparkles: Star };

export default function Ecosystem() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-40 pb-28 min-h-[65vh] flex items-center overflow-hidden bg-[#080808]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1698303098477-fac2428d9dfe?w=1600&q=85"
            alt="ALV Ecosystem"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.4)] to-[#080808]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <p className="eyebrow mb-5">The ALV Ecosystem</p>
            <h1 className="heading-display text-[clamp(3rem,7vw,6rem)] text-[#f5f5f5] max-w-4xl mb-6">
              From Discovery to
              <br />
              <span className="text-gold italic">World Stage</span>
            </h1>
            <div className="divider-gold-left" />
            <p className="mt-6 text-[#f5f5f5]/55 text-base md:text-lg max-w-2xl leading-relaxed">
              A vertically integrated pipeline that covers every stage of talent's journey — ensuring world-class outcomes at every step.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pipeline visual */}
      <section className="py-24 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          {/* Connected step flow */}
          <div className="relative">
            {/* Vertical connecting line (desktop) */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[rgba(201,168,76,0.2)] to-transparent" />

            <div className="space-y-16">
              {ECOSYSTEM_STEPS.map((step, i) => {
                const Icon = ICONS[step.icon as keyof typeof ICONS] ?? Star;
                const company = COMPANIES.find(c => c.slug === step.companySlug);
                const isLeft = i % 2 === 0;
                return (
                  <FadeUp key={step.step} delay={i * 0.1}>
                    <div className={`flex flex-col lg:flex-row ${!isLeft ? 'lg:flex-row-reverse' : ''} gap-12 items-center`}>
                      {/* Text side */}
                      <div className={`flex-1 ${isLeft ? 'lg:text-right' : 'lg:text-left'}`}>
                        <div className={`flex items-start gap-5 ${isLeft ? 'lg:flex-row-reverse' : ''}`}>
                          <div>
                            <div className={`flex items-center gap-3 mb-3 ${isLeft ? 'lg:justify-end' : ''}`}>
                              <p className="eyebrow">{step.company}</p>
                            </div>
                            <h3 className="heading-display text-3xl md:text-4xl text-[#f5f5f5] mb-4">
                              {step.title}
                            </h3>
                            <p className="text-[#f5f5f5]/55 text-sm leading-relaxed max-w-sm">
                              {step.description}
                            </p>
                            <Link
                              to={companyPath(step.companySlug)}
                              className={`inline-flex items-center gap-2 mt-5 text-[11px] tracking-[0.2em] uppercase text-[#c9a84c] hover:text-[#f0c040] transition-colors duration-300 ${isLeft ? 'lg:flex-row-reverse' : ''}`}
                            >
                              <span>Enter {step.company}</span>
                              <ArrowRight size={12} />
                            </Link>
                          </div>
                        </div>
                      </div>

                      {/* Center node */}
                      <div className="flex-shrink-0 z-10">
                        <div className="w-20 h-20 border-2 border-[rgba(201,168,76,0.4)] bg-[#0e0e0e] flex items-center justify-center flex-col gap-1 shadow-[0_0_40px_rgba(201,168,76,0.12)]">
                          <Icon size={20} className="text-[#c9a84c]" />
                          <span className="text-[9px] tracking-[0.2em] text-[#c9a84c]/60 font-medium">{step.step}</span>
                        </div>
                      </div>

                      {/* Image side */}
                      <div className="flex-1">
                        <div className="card-editorial h-64 lg:h-80">
                          <img
                            src={company?.portfolioItems[0]?.image ?? company?.heroImage ?? ''}
                            alt={step.title}
                            loading="lazy"
                          />
                          <div className="card-overlay" />
                          <div className="absolute bottom-5 left-5">
                            <span className="eyebrow text-[9px]">{step.step} / {ECOSYSTEM_STEPS.length.toString().padStart(2, '0')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Companies in ecosystem */}
      <section className="py-24 bg-[#060606]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <FadeUp className="text-center mb-14">
            <p className="eyebrow mb-4">The Five Pillars</p>
            <h2 className="heading-display text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f5]">
              Companies in the Ecosystem
            </h2>
            <div className="divider-gold mt-5" />
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-[rgba(201,168,76,0.08)]">
            {COMPANIES.map((company, i) => (
              <FadeUp key={company.id} delay={i * 0.08}>
                <div className="bg-[#090909] p-8 flex flex-col h-full group hover:bg-[rgba(201,168,76,0.03)] transition-colors duration-300">
                  <div
                    className="w-10 h-10 flex items-center justify-center border text-[10px] font-bold tracking-widest mb-5"
                    style={{ borderColor: 'rgba(201,168,76,0.4)', color: '#c9a84c', fontFamily: 'var(--font-sans)' }}
                  >
                    {company.logoInitials}
                  </div>
                  <h4 className="heading-display text-xl text-[#f5f5f5] mb-3 flex-grow">{company.name}</h4>
                  <div className="divider-gold-left mb-4" />
                  <p className="text-[#f5f5f5]/45 text-xs leading-relaxed mb-5">{company.tagline}</p>
                  <Link
                    to={companyPath(company.slug)}
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#c9a84c] hover:text-[#f0c040] transition-colors duration-300"
                  >
                    Enter Site <ArrowRight size={10} />
                  </Link>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 bg-[#080808] text-center">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <FadeUp>
            <p className="eyebrow mb-6">Be Part of It</p>
            <h2 className="heading-display text-[clamp(2.5rem,5vw,4.5rem)] text-[#f5f5f5] mb-8">
              Ready to Enter the Ecosystem?
            </h2>
            <div className="divider-gold mb-8" />
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={ROUTE_PATHS.CONTACT} className="btn-gold-fill">Apply Now</Link>
              <Link to={ROUTE_PATHS.COMPANIES} className="btn-gold-outline">View Companies</Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </Layout>
  );
}
